import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Game.css';
import audio from '../utils/audio';

const BASE_CELL_SIZE = 20;
const ROWS = 21;
const COLS = 19;

// Classic Pacman maze layout (19 columns × 21 rows)
// 1 = Wall, 0 = Empty path, 2 = Dot, 3 = Power pellet
const MAZE = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 3, 1, 1, 1, 2, 1, 2, 1, 2, 1, 2, 1, 1, 1, 2, 1, 3, 1],
  [1, 2, 2, 2, 2, 2, 1, 2, 1, 2, 1, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 2, 1, 1, 1, 2, 1, 2, 1, 2, 1, 2, 1, 1, 1, 2, 1, 2, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 1, 1, 1, 1, 2, 1, 1, 1, 0, 1, 1, 1, 2, 1, 1, 1, 1, 1],
  [0, 0, 0, 0, 1, 2, 1, 0, 0, 0, 0, 0, 1, 2, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 2, 1, 0, 1, 1, 1, 0, 1, 2, 1, 1, 1, 1, 1],
  [0, 0, 0, 0, 0, 2, 0, 0, 1, 0, 1, 0, 0, 2, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 2, 1, 0, 1, 1, 1, 0, 1, 2, 1, 1, 1, 1, 1],
  [0, 0, 0, 0, 1, 2, 1, 0, 0, 0, 0, 0, 1, 2, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 2, 1, 0, 1, 1, 1, 0, 1, 2, 1, 1, 1, 1, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 2, 1, 1, 1, 2, 1, 1, 1, 2, 1, 1, 1, 2, 1, 1, 1, 2, 1],
  [1, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 1],
  [1, 1, 2, 1, 1, 2, 1, 2, 1, 1, 1, 2, 1, 2, 1, 1, 2, 1, 1],
  [1, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 1],
  [1, 3, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 3, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
];

const DIRECTIONS = {
  UP: { x: 0, y: -1 },
  DOWN: { x: 0, y: 1 },
  LEFT: { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 }
};

const GHOST_START_POSITIONS = [
  { id: 0, x: 9, y: 8, dir: 'UP', color: '#FF0000', emotion: 'FEAR' },
  { id: 1, x: 8, y: 9, dir: 'UP', color: '#FFB8FF', emotion: 'ANXIETY' },
  { id: 2, x: 10, y: 9, dir: 'UP', color: '#00FFFF', emotion: 'STRESS' },
  { id: 3, x: 9, y: 10, dir: 'UP', color: '#FFB851', emotion: 'DOUBT' }
];

// Respawn position outside ghost house where ghosts can immediately chase Pacman
const GHOST_RESPAWN_POSITION = { x: 9, y: 6, dir: 'DOWN' };

// Corner positions for extra power pellet spawn (top-left, top-right, bottom-left, bottom-right)
const POWER_PELLET_CORNERS = [
  { x: 1, y: 2 },   // Top-left
  { x: 17, y: 2 },  // Top-right
  { x: 1, y: 18 },  // Bottom-left
  { x: 17, y: 18 }  // Bottom-right
];

const WISDOM_MESSAGES = [
  "Connect before you correct.",
  "Align before you design.",
  "You can't NOT be in a frequency.",
  "Focus creates feelings.",
  "Your natural state is feeling good about you.",
  "Confidence is what you get on the other side of fear.",
  "Fear is a battery — connect the terminals and it becomes power.",
  "Do it scared until it becomes exciting.",
  "Those uncomfortable sensations aren't resistance — they're signals to advance.",
  "You don't want the thing — you want what's inside the thing.",
  "What if I'm being designed by my future self?",
  "If you know it, that's all that's required — go first.",
  "Go wish-hunting. You do know what you want.",
  "You can't give positive energy away — it comes back too fast.",
  "Live in the win.",
  "Never sacrifice your vibe.",
  "You're not here for anyone else — you're here with everyone else.",
  "Stop overidentifying with the negative signal — it's just feedback.",
  "Unfamiliar ways of looking at familiar situations — that's evolution.",
  "Be the main character, not the supporting character in someone else's movie.",
  "Let yourself play the way you want to play."
];

function Game() {
  const [cellSize, setCellSize] = useState(BASE_CELL_SIZE);
  const [pacmanPos, setPacmanPos] = useState({ x: 9, y: 15 });
  const [pacmanDir, setPacmanDir] = useState('RIGHT');
  const [queuedDir, setQueuedDir] = useState(null);

  const [ghosts, setGhosts] = useState(
    GHOST_START_POSITIONS.map(g => ({ ...g, scared: false }))
  );
  const [dots, setDots] = useState(new Set());
  const [powerPellets, setPowerPellets] = useState(new Set());
  const [score, setScore] = useState(0);
  const [powerMode, setPowerMode] = useState(false);
  const [powerModeTime, setPowerModeTime] = useState(0);
  const [consumedPowerPellets, setConsumedPowerPellets] = useState(0);
  const [gameState, setGameState] = useState('playing'); // playing, gameOver, gameWon
  const [mouthOpen, setMouthOpen] = useState(true);
  const [wisdomMessage, setWisdomMessage] = useState(null);
  const [isMuted, setIsMuted] = useState(true); // Default to muted
  const [audioInitialized, setAudioInitialized] = useState(false);

  const gameLoopRef = useRef(null);
  const powerModeTimerRef = useRef(null);
  const mouthAnimationRef = useRef(null);
  const wisdomTimeoutRef = useRef(null);
  const audioInitializedRef = useRef(false);
  const lastWisdomIndexRef = useRef(null);
  const wisdomCountsRef = useRef(new Array(WISDOM_MESSAGES.length).fill(0));
  const lastSpawnCornerRef = useRef(null);
  const processedCollisionsRef = useRef(new Set());
  const touchStartRef = useRef(null);

  // Calculate cell size based on viewport to fill screen
  useEffect(() => {
    const calculateCellSize = () => {
      // Detect if mobile/touch device
      const isMobile = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768;

      // Reserve space for different elements
      let reservedHeight = 0;
      if (isMobile) {
        // Mobile: Header, controls instruction, wisdom message OR legend, touch controls, padding
        // - Header: ~50px (smaller on mobile)
        // - Controls instruction: ~25px
        // - Wisdom message OR legend: ~100px (whichever is visible)
        // - Touch controls: ~100px
        // - Padding: ~30px
        reservedHeight = 305; // Account for either wisdom message or legend
      } else {
        // Desktop: Header, controls instruction, legend, padding
        // - Header: ~60px
        // - Controls instruction: ~30px
        // - Legend: ~100px
        // - Padding: ~40px
        reservedHeight = 230;
      }

      const availableHeight = window.innerHeight - reservedHeight;
      const availableWidth = window.innerWidth - (isMobile ? 20 : 40);

      // Calculate cell size based on both dimensions, maintaining aspect ratio
      const cellSizeByHeight = availableHeight / ROWS;
      const cellSizeByWidth = availableWidth / COLS;

      // Use the smaller to ensure everything fits and maintain aspect ratio
      const calculatedSize = Math.floor(Math.min(cellSizeByHeight, cellSizeByWidth));

      // Ensure minimum size and use calculated size
      setCellSize(Math.max(calculatedSize, BASE_CELL_SIZE));
    };

    calculateCellSize();
    window.addEventListener('resize', calculateCellSize);
    return () => window.removeEventListener('resize', calculateCellSize);
  }, []);

  // Initialize collectibles from maze
  useEffect(() => {
    const initialDots = new Set();
    const initialPellets = new Set();

    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (MAZE[y][x] === 2) {
          initialDots.add(`${x},${y}`);
        } else if (MAZE[y][x] === 3) {
          initialPellets.add(`${x},${y}`);
        }
      }
    }

    setDots(initialDots);
    setPowerPellets(initialPellets);
  }, []);

  // Mouth animation
  useEffect(() => {
    mouthAnimationRef.current = setInterval(() => {
      setMouthOpen(prev => !prev);
    }, 300);
    return () => {
      if (mouthAnimationRef.current) {
        clearInterval(mouthAnimationRef.current);
      }
    };
  }, []);

  // Cleanup wisdom timeout on unmount
  useEffect(() => {
    return () => {
      if (wisdomTimeoutRef.current) {
        clearTimeout(wisdomTimeoutRef.current);
      }
    };
  }, []);

  // Function to get next wisdom message with equal distribution and no consecutive repeats
  const getNextWisdomMessage = () => {
    const counts = wisdomCountsRef.current;
    const lastIndex = lastWisdomIndexRef.current;

    // Find the minimum count
    const minCount = Math.min(...counts);

    // Get all indices with minimum count, excluding the last shown one
    const availableIndices = counts
      .map((count, index) => ({ count, index }))
      .filter(({ count, index }) => count === minCount && index !== lastIndex)
      .map(({ index }) => index);

    // If no available indices (only one message or last one is the only one with min count),
    // allow all messages except the last one
    let candidateIndices = availableIndices.length > 0
      ? availableIndices
      : counts.map((_, index) => index).filter(index => index !== lastIndex);

    // If somehow all messages are excluded (shouldn't happen with >1 message), allow all
    if (candidateIndices.length === 0) {
      candidateIndices = counts.map((_, index) => index);
    }

    // Randomly select from candidates
    const randomIndex = candidateIndices[Math.floor(Math.random() * candidateIndices.length)];

    // Update counts
    counts[randomIndex]++;

    // Check if all messages have been shown equally (all have same count)
    const allSameCount = counts.every(count => count === counts[0]);
    if (allSameCount && counts[0] > 0) {
      // Reset counts for next cycle
      wisdomCountsRef.current = new Array(WISDOM_MESSAGES.length).fill(0);
    }

    // Update last shown index
    lastWisdomIndexRef.current = randomIndex;

    return WISDOM_MESSAGES[randomIndex];
  };

  // Find the corner farthest from Pacman (excluding the last spawn corner)
  const getFarthestCorner = useCallback((pacmanX, pacmanY) => {
    // Filter out the last spawn corner
    const availableCorners = POWER_PELLET_CORNERS.filter(corner => {
      const cornerKey = `${corner.x},${corner.y}`;
      const lastCornerKey = lastSpawnCornerRef.current ? `${lastSpawnCornerRef.current.x},${lastSpawnCornerRef.current.y}` : null;
      return cornerKey !== lastCornerKey;
    });

    // If all corners were excluded (shouldn't happen), use all corners
    const cornersToCheck = availableCorners.length > 0 ? availableCorners : POWER_PELLET_CORNERS;

    // Find the corner with maximum distance from Pacman
    let farthestCorner = cornersToCheck[0];
    let maxDistance = 0;

    for (const corner of cornersToCheck) {
      // Calculate Manhattan distance (or Euclidean, but Manhattan is simpler and works well for grid)
      const distance = Math.abs(corner.x - pacmanX) + Math.abs(corner.y - pacmanY);
      if (distance > maxDistance) {
        maxDistance = distance;
        farthestCorner = corner;
      }
    }

    return farthestCorner;
  }, []);

  // Check if position is valid (not a wall)
  const isValidPosition = useCallback((x, y) => {
    if (x < 0 || x >= COLS || y < 0 || y >= ROWS) return false;
    return MAZE[y][x] !== 1;
  }, []);

  // Get next position in direction
  const getNextPos = useCallback((x, y, dir) => {
    const delta = DIRECTIONS[dir];
    return { x: x + delta.x, y: y + delta.y };
  }, []);

  // Move Pacman
  const movePacman = useCallback(() => {
    setPacmanPos(prev => {
      // Try queued direction first
      if (queuedDir) {
        const nextPos = getNextPos(prev.x, prev.y, queuedDir);
        if (isValidPosition(nextPos.x, nextPos.y)) {
          setPacmanDir(queuedDir);
          setQueuedDir(null);
          return nextPos;
        }
      }

      // Try current direction
      const nextPos = getNextPos(prev.x, prev.y, pacmanDir);
      if (isValidPosition(nextPos.x, nextPos.y)) {
        return nextPos;
      }

      return prev;
    });
  }, [pacmanDir, queuedDir, getNextPos, isValidPosition]);

  // Handle direction change (reusable for keyboard and touch)
  const handleDirectionChange = useCallback((newDir) => {
    if (gameState !== 'playing') return;

    const nextPos = getNextPos(pacmanPos.x, pacmanPos.y, newDir);
    if (isValidPosition(nextPos.x, nextPos.y)) {
      setPacmanDir(newDir);
      setQueuedDir(null);
    } else {
      setQueuedDir(newDir);
    }
  }, [gameState, pacmanPos, getNextPos, isValidPosition]);

  // Move ghost
  const moveGhost = useCallback((ghost) => {
    const possibleDirs = Object.keys(DIRECTIONS);
    const validDirs = possibleDirs.filter(dir => {
      const nextPos = getNextPos(ghost.x, ghost.y, dir);
      return isValidPosition(nextPos.x, nextPos.y) && dir !== getOppositeDir(ghost.dir);
    });

    if (validDirs.length === 0) {
      // No valid directions, allow reverse
      const nextPos = getNextPos(ghost.x, ghost.y, ghost.dir);
      if (isValidPosition(nextPos.x, nextPos.y)) {
        return { ...ghost, ...nextPos, id: ghost.id, color: ghost.color, emotion: ghost.emotion };
      }
      // If even reverse is blocked, try any direction
      const allDirs = possibleDirs.filter(dir => {
        const nextPos = getNextPos(ghost.x, ghost.y, dir);
        return isValidPosition(nextPos.x, nextPos.y);
      });
      if (allDirs.length > 0) {
        const nextPos = getNextPos(ghost.x, ghost.y, allDirs[0]);
        return { ...ghost, ...nextPos, dir: allDirs[0], id: ghost.id, color: ghost.color, emotion: ghost.emotion };
      }
      return ghost;
    }

    // Choose direction based on AI
    let chosenDir = validDirs[0];
    if (gameState === 'playing') {
      if (ghost.scared) {
        // Flee from Pacman
        chosenDir = validDirs.reduce((best, dir) => {
          const pos = getNextPos(ghost.x, ghost.y, dir);
          const bestPos = getNextPos(ghost.x, ghost.y, best);
          const distBest = Math.abs(bestPos.x - pacmanPos.x) + Math.abs(bestPos.y - pacmanPos.y);
          const distDir = Math.abs(pos.x - pacmanPos.x) + Math.abs(pos.y - pacmanPos.y);
          return distDir > distBest ? dir : best;
        });
      } else {
        // Chase Pacman
        chosenDir = validDirs.reduce((best, dir) => {
          const pos = getNextPos(ghost.x, ghost.y, dir);
          const bestPos = getNextPos(ghost.x, ghost.y, best);
          const distBest = Math.abs(bestPos.x - pacmanPos.x) + Math.abs(bestPos.y - pacmanPos.y);
          const distDir = Math.abs(pos.x - pacmanPos.x) + Math.abs(pos.y - pacmanPos.y);
          return distDir < distBest ? dir : best;
        });
      }
    }

    const nextPos = getNextPos(ghost.x, ghost.y, chosenDir);

    // Check if the chosen direction leads to a valid position
    if (!isValidPosition(nextPos.x, nextPos.y)) {
      // If chosen direction is invalid, pick a random valid direction to avoid getting stuck
      const randomDir = validDirs[Math.floor(Math.random() * validDirs.length)];
      const randomNextPos = getNextPos(ghost.x, ghost.y, randomDir);
      return { ...ghost, ...randomNextPos, dir: randomDir, id: ghost.id, color: ghost.color, emotion: ghost.emotion };
    }

    return { ...ghost, ...nextPos, dir: chosenDir, id: ghost.id, color: ghost.color, emotion: ghost.emotion };
  }, [pacmanPos, gameState, getNextPos, isValidPosition]);

  const getOppositeDir = (dir) => {
    const opposites = {
      UP: 'DOWN',
      DOWN: 'UP',
      LEFT: 'RIGHT',
      RIGHT: 'LEFT'
    };
    return opposites[dir];
  };

  // Check collisions
  useEffect(() => {
    if (gameState !== 'playing') return;

    // Clear processed collisions at the start of each effect run
    processedCollisionsRef.current.clear();

    // Check collectibles
    const posKey = `${pacmanPos.x},${pacmanPos.y}`;
    if (dots.has(posKey)) {
      setDots(prev => {
        const newSet = new Set(prev);
        newSet.delete(posKey);
        return newSet;
      });
      setScore(prev => prev + 10);
      // Play sound effect (fire and forget)
      audio.playDotEat().catch(() => { });
    }
    if (powerPellets.has(posKey)) {
      setPowerPellets(prev => {
        const newSet = new Set(prev);
        newSet.delete(posKey);
        // If all power pellets are collected, spawn an extra one in the farthest corner
        if (newSet.size === 0) {
          const spawnCorner = getFarthestCorner(pacmanPos.x, pacmanPos.y);
          const spawnKey = `${spawnCorner.x},${spawnCorner.y}`;
          newSet.add(spawnKey);
          // Track the last spawn corner to avoid repeating it
          lastSpawnCornerRef.current = spawnCorner;
        }
        return newSet;
      });
      setConsumedPowerPellets(prev => prev + 1);
      setScore(prev => prev + 50);
      setPowerMode(true);
      setPowerModeTime(200); // 20 seconds = 200 * 100ms
      setGhosts(prev => prev.map(g => ({ ...g, scared: true, id: g.id, color: g.color, emotion: g.emotion })));
      // Play sound effect (fire and forget)
      audio.playPowerPellet().catch(() => { });

      // Display wisdom message with equal distribution
      setWisdomMessage(getNextWisdomMessage());

      // Clear any existing timeout
      if (wisdomTimeoutRef.current) {
        clearTimeout(wisdomTimeoutRef.current);
      }

      // Clear message after 5 seconds
      wisdomTimeoutRef.current = setTimeout(() => {
        setWisdomMessage(null);
      }, 5000);
    }

    // Check ghost collisions
    // Process all collisions at the same position before breaking
    const collisionPosition = { x: pacmanPos.x, y: pacmanPos.y };
    let gameOverTriggered = false;

    for (let i = 0; i < ghosts.length; i++) {
      // Skip if this ghost has already been processed in this frame
      if (processedCollisionsRef.current.has(i)) {
        continue;
      }

      const ghost = ghosts[i];

      // Check if ghost is at collision position
      if (ghost.x === collisionPosition.x && ghost.y === collisionPosition.y) {
        if (ghost.scared) {
          // Mark this ghost as processed to prevent double-processing
          processedCollisionsRef.current.add(i);

          // Eat ghost - respawn a DIFFERENT random ghost outside ghost house
          // Get all available ghost types except the one that was eaten
          const eatenGhostId = ghost.id !== undefined && ghost.id !== null ? ghost.id : i;
          const availableGhosts = GHOST_START_POSITIONS.filter((g, idx) => g.id !== eatenGhostId);

          // Pick a random different ghost to respawn
          const randomIndex = Math.floor(Math.random() * availableGhosts.length);
          const newGhostType = availableGhosts[randomIndex];

          if (newGhostType) {
            setScore(prev => prev + 200);
            // Play sound effect (fire and forget)
            audio.playGhostEat().catch(() => { });

            // Display wisdom message with equal distribution when ghost is eaten
            setWisdomMessage(getNextWisdomMessage());

            // Clear any existing timeout
            if (wisdomTimeoutRef.current) {
              clearTimeout(wisdomTimeoutRef.current);
            }

            // Clear message after 5 seconds
            wisdomTimeoutRef.current = setTimeout(() => {
              setWisdomMessage(null);
            }, 5000);

            // Use functional update with defensive check to ensure ghost is still at collision position
            setGhosts(prev => {
              const newGhosts = [...prev];
              // Defensive check: ensure the ghost at index i is still at the collision position
              // This prevents race conditions where the ghost might have moved between collision check and state update
              if (newGhosts[i] && newGhosts[i].x === collisionPosition.x && newGhosts[i].y === collisionPosition.y && newGhosts[i].scared) {
                // Respawn outside ghost house so it can immediately chase Pacman
                newGhosts[i] = {
                  id: newGhostType.id,
                  x: GHOST_RESPAWN_POSITION.x,
                  y: GHOST_RESPAWN_POSITION.y,
                  dir: GHOST_RESPAWN_POSITION.dir,
                  scared: false,
                  emotion: newGhostType.emotion,
                  color: newGhostType.color
                };
              }
              return newGhosts;
            });
          }
        } else {
          // Game over - only trigger once
          if (!gameOverTriggered) {
            gameOverTriggered = true;
            setGameState('gameOver');
            // Play sound effect (fire and forget)
            audio.playGameOver().catch(() => { });
          }
          break; // Exit immediately on game over
        }
      }
    }

    // Check win condition
    if (dots.size === 0 && consumedPowerPellets >= 3) {
      setGameState('gameWon');
      // Play sound effect (fire and forget)
      audio.playGameWin().catch(() => { });
    }
  }, [pacmanPos, dots, powerPellets, ghosts, gameState, getFarthestCorner, consumedPowerPellets]);

  // Background music control
  useEffect(() => {
    const handleMusic = async () => {
      if (gameState === 'playing' && !isMuted) {
        // Ensure audio is ready before playing music
        if (!audioInitialized) {
          audio.initAudioContext();
          const ready = await audio.ensureAudioReady();
          if (ready) {
            setAudioInitialized(true);
            audioInitializedRef.current = true;
          }
        }

        // Small delay to ensure audio context is fully ready
        if (audioInitialized || audioInitializedRef.current) {
          // Try playing music, retry once if it fails
          try {
            await audio.playBackgroundMusic();
          } catch (e) {
            console.warn('Background music failed, retrying...', e);
            setTimeout(async () => {
              await audio.ensureAudioReady();
              await audio.playBackgroundMusic();
            }, 100);
          }
        }
      } else {
        audio.stopBackgroundMusic();
      }
    };

    handleMusic();

    return () => {
      if (gameState !== 'playing') {
        audio.stopBackgroundMusic();
      }
    };
  }, [gameState, isMuted, audioInitialized]);

  // Initialize audio on first user interaction
  useEffect(() => {
    const initAudio = async () => {
      if (!audioInitializedRef.current) {
        audio.initAudioContext();
        // Ensure audio is ready
        const ready = await audio.ensureAudioReady();
        if (ready) {
          setAudioInitialized(true);
          audioInitializedRef.current = true;
        }
        // Remove listeners after first interaction
        document.removeEventListener('click', initAudio);
        document.removeEventListener('keydown', initAudio);
      }
    };

    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('keydown', initAudio, { once: true });

    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
    };
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      audio.cleanup();
    };
  }, []);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
      return;
    }

    gameLoopRef.current = setInterval(() => {
      movePacman();
      setGhosts(prev => prev.map(ghost => moveGhost(ghost)));
    }, 300);

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameState, movePacman, moveGhost]);

  // Power mode timer
  useEffect(() => {
    if (!powerMode) {
      if (powerModeTimerRef.current) {
        clearInterval(powerModeTimerRef.current);
        powerModeTimerRef.current = null;
      }
      return;
    }

    powerModeTimerRef.current = setInterval(() => {
      setPowerModeTime(prev => {
        if (prev <= 1) {
          setPowerMode(false);
          setGhosts(prevGhosts => prevGhosts.map(g => ({ ...g, scared: false, id: g.id, color: g.color, emotion: g.emotion })));
          return 0;
        }
        return prev - 1;
      });
    }, 100);

    return () => {
      if (powerModeTimerRef.current) {
        clearInterval(powerModeTimerRef.current);
      }
    };
  }, [powerMode]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      // 'T' key shortcut to trigger win (for testing)
      if (e.key === 't' || e.key === 'T') {
        if (gameState === 'playing') {
          setGameState('gameWon');
          audio.playGameWin().catch(() => { });
        }
        return;
      }

      if (gameState !== 'playing') return;

      const keyMap = {
        'ArrowUp': 'UP',
        'ArrowDown': 'DOWN',
        'ArrowLeft': 'LEFT',
        'ArrowRight': 'RIGHT',
        'w': 'UP',
        'W': 'UP',
        's': 'DOWN',
        'S': 'DOWN',
        'a': 'LEFT',
        'A': 'LEFT',
        'd': 'RIGHT',
        'D': 'RIGHT'
      };

      const newDir = keyMap[e.key];
      if (newDir) {
        handleDirectionChange(newDir);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, handleDirectionChange]);

  // Touch/swipe gesture controls
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (gameState !== 'playing') return;
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY
      };
    };

    const handleTouchEnd = (e) => {
      if (gameState !== 'playing' || !touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const minSwipeDistance = 30;

      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Check if swipe distance is sufficient
      if (absDeltaX < minSwipeDistance && absDeltaY < minSwipeDistance) {
        touchStartRef.current = null;
        return;
      }

      // Determine direction based on larger delta
      let newDir = null;
      if (absDeltaX > absDeltaY) {
        // Horizontal swipe
        newDir = deltaX > 0 ? 'RIGHT' : 'LEFT';
      } else {
        // Vertical swipe
        newDir = deltaY > 0 ? 'DOWN' : 'UP';
      }

      if (newDir) {
        handleDirectionChange(newDir);
      }

      touchStartRef.current = null;
    };

    const mazeContainer = document.querySelector('.maze-container');
    if (mazeContainer) {
      mazeContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
      mazeContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (mazeContainer) {
        mazeContainer.removeEventListener('touchstart', handleTouchStart);
        mazeContainer.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [gameState, handleDirectionChange]);

  // Toggle mute
  const toggleMute = async () => {
    // Ensure audio context is initialized when user clicks mute button
    if (!audioInitialized) {
      audio.initAudioContext();
      const ready = await audio.ensureAudioReady();
      if (ready) {
        setAudioInitialized(true);
        audioInitializedRef.current = true;
      }
    } else {
      // Make sure audio is ready even if already initialized
      await audio.ensureAudioReady();
    }

    const newMutedState = audio.toggleMute();
    setIsMuted(newMutedState);
  };


  // Reset game
  const resetGame = () => {
    setPacmanPos({ x: 9, y: 15 });
    setPacmanDir('RIGHT');
    setQueuedDir(null);
    setGhosts(GHOST_START_POSITIONS.map(g => ({ ...g, scared: false, color: g.color, emotion: g.emotion })));
    setScore(0);
    setPowerMode(false);
    setPowerModeTime(0);
    setConsumedPowerPellets(0);
    setGameState('playing');
    setWisdomMessage(null);

    // Clear wisdom timeout if exists
    if (wisdomTimeoutRef.current) {
      clearTimeout(wisdomTimeoutRef.current);
      wisdomTimeoutRef.current = null;
    }

    // Reset wisdom message tracking
    lastWisdomIndexRef.current = null;
    wisdomCountsRef.current = new Array(WISDOM_MESSAGES.length).fill(0);

    // Reset last spawn corner tracking
    lastSpawnCornerRef.current = null;

    // Reset collectibles
    const initialDots = new Set();
    const initialPellets = new Set();
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (MAZE[y][x] === 2) {
          initialDots.add(`${x},${y}`);
        } else if (MAZE[y][x] === 3) {
          initialPellets.add(`${x},${y}`);
        }
      }
    }
    setDots(initialDots);
    setPowerPellets(initialPellets);
  };

  // Proportional sizes based on cell size
  const pacmanSize = (cellSize * 16) / BASE_CELL_SIZE;
  const ghostWidth = (cellSize * 18) / BASE_CELL_SIZE;
  const ghostHeight = (cellSize * 16) / BASE_CELL_SIZE;
  const dotSize = (cellSize * 4) / BASE_CELL_SIZE;
  const pelletSize = (cellSize * 10) / BASE_CELL_SIZE;
  const eyeSize = (cellSize * 4) / BASE_CELL_SIZE;
  const pupilSize = (cellSize * 2) / BASE_CELL_SIZE;
  const borderWidth = Math.max(2, (cellSize * 2) / BASE_CELL_SIZE);
  const ghostTopRadius = (cellSize * 8) / BASE_CELL_SIZE;
  const ghostBottomRadius = (cellSize * 2) / BASE_CELL_SIZE;
  const eyeTop = (cellSize * 3) / BASE_CELL_SIZE;
  const eyeLeft = (cellSize * 3) / BASE_CELL_SIZE;
  const eyeRight = (cellSize * 3) / BASE_CELL_SIZE;

  // Render cell content
  const renderCell = (x, y) => {
    const cellType = MAZE[y][x];
    const cellKey = `${x},${y}`;
    const hasDot = dots.has(cellKey);
    const hasPellet = powerPellets.has(cellKey);
    const isPacman = pacmanPos.x === x && pacmanPos.y === y;
    const cellGhosts = ghosts.filter(g => g.x === x && g.y === y);

    return (
      <div
        key={`${x}-${y}`}
        className={`cell cell-type-${cellType}`}
        style={{
          width: cellSize,
          height: cellSize
        }}
      >
        {hasPellet && (
          <div
            className="power-pellet"
            style={{
              width: pelletSize,
              height: pelletSize
            }}
          />
        )}
        {hasDot && !hasPellet && (
          <div
            className="dot"
            style={{
              width: dotSize,
              height: dotSize
            }}
          />
        )}
        {isPacman && (
          <div
            className={`pacman pacman-${pacmanDir.toLowerCase()} ${mouthOpen ? 'mouth-open' : 'mouth-closed'}`}
            style={{
              width: pacmanSize,
              height: pacmanSize
            }}
          />
        )}
        {cellGhosts.map(ghost => {
          const ghostStartPos = GHOST_START_POSITIONS.find(g => g.id === ghost.id);
          const emotion = ghostStartPos ? ghostStartPos.emotion : '';
          return (
            <div
              key={ghost.id}
              className={`ghost ${ghost.scared ? 'scared' : ''} ghost-emotion-${emotion.toLowerCase()}`}
              style={{
                backgroundColor: ghost.scared ? '#2121DE' : (ghost.color || (ghostStartPos ? ghostStartPos.color : '#FF0000')),
                width: ghostWidth,
                height: ghostHeight,
                borderRadius: `${ghostTopRadius}px ${ghostTopRadius}px ${ghostBottomRadius}px ${ghostBottomRadius}px`,
                paddingTop: `${(cellSize * 2) / BASE_CELL_SIZE}px`,
                left: `${(cellSize - ghostWidth) / 2}px`
              }}
            >
              {!ghost.scared && (
                <>
                  <div
                    className="ghost-eye ghost-eye-left"
                    style={{
                      width: eyeSize,
                      height: eyeSize,
                      top: eyeTop,
                      left: eyeLeft
                    }}
                  >
                    <div
                      className="ghost-pupil"
                      style={{
                        width: pupilSize,
                        height: pupilSize
                      }}
                    />
                  </div>
                  <div
                    className="ghost-eye ghost-eye-right"
                    style={{
                      width: eyeSize,
                      height: eyeSize,
                      top: eyeTop,
                      right: eyeRight
                    }}
                  >
                    <div
                      className="ghost-pupil"
                      style={{
                        width: pupilSize,
                        height: pupilSize
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const mazeWidth = COLS * cellSize;
  const headerMaxWidth = mazeWidth + (borderWidth * 2);

  return (
    <div className="game-container">
      <div
        className="game-header"
        style={{ maxWidth: `${headerMaxWidth}px` }}
      >
        <div className="score">Score: {score}</div>
        <div className="header-right">
          {powerMode && (
            <div className="power-mode-indicator">
              POWER MODE! ({Math.ceil(powerModeTime / 10)}s)
            </div>
          )}
          <button
            className="audio-toggle-button"
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>
      <div className="maze-container">
        <div
          className="maze"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${COLS}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${ROWS}, ${cellSize}px)`,
            gap: 0,
            borderWidth: `${borderWidth}px`
          }}
        >
          {Array.from({ length: ROWS }, (_, y) =>
            Array.from({ length: COLS }, (_, x) => renderCell(x, y))
          )}
        </div>
        {gameState === 'gameWon' && (
          <img
            src="/jesse_elder_wink.gif"
            alt="Jesse Elder Win"
            className="maze-win-gif"
          />
        )}
        {(gameState === 'gameOver' || gameState === 'gameWon') && (
          <>
            <button className="play-again-button-bottom-right" onClick={resetGame}>
              Play Again
            </button>
            <div className="overlay-content-bottom-left">
              <div className="overlay-title">
                {gameState === 'gameOver' ? 'Game Over' : 'You Win!'}
              </div>
              <div className="overlay-score">Final Score: {score}</div>
            </div>
          </>
        )}
      </div>
      <div className="controls-instruction">Use Arrow Keys, WASD, or touch/swipe to move</div>
      <div className="mobile-bottom-container">
        <div className="message-legend-container">
          {wisdomMessage ? (
            <div className="wisdom-message-container">
              <div className="wisdom-content">
                <img
                  src="/jesseelder.svg"
                  alt="Jesse Elder"
                  className="wisdom-image"
                  style={{
                    width: `${(cellSize * 30) / BASE_CELL_SIZE}px`,
                    height: `${(cellSize * 28) / BASE_CELL_SIZE}px`,
                    flexShrink: 0
                  }}
                />
                <div className="wisdom-speech-bubble">
                  <div className="wisdom-message">
                    {wisdomMessage}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ghost-legend">
              <div className="ghost-legend-items">
                {GHOST_START_POSITIONS.map(ghost => {
                  const legendGhostWidth = (cellSize * 18) / BASE_CELL_SIZE;
                  const legendGhostHeight = (cellSize * 16) / BASE_CELL_SIZE;
                  const legendGhostTopRadius = (cellSize * 8) / BASE_CELL_SIZE;
                  const legendGhostBottomRadius = (cellSize * 2) / BASE_CELL_SIZE;
                  const legendEyeSize = (cellSize * 4) / BASE_CELL_SIZE;
                  const legendPupilSize = (cellSize * 2) / BASE_CELL_SIZE;
                  const legendEyeTop = (cellSize * 3) / BASE_CELL_SIZE;
                  const legendEyeLeft = (cellSize * 3) / BASE_CELL_SIZE;
                  const legendEyeRight = (cellSize * 3) / BASE_CELL_SIZE;

                  return (
                    <div key={ghost.id} className="ghost-legend-item">
                      <div
                        className={`ghost-legend-icon ghost-emotion-${ghost.emotion.toLowerCase()}`}
                        style={{
                          backgroundColor: ghost.color,
                          width: legendGhostWidth,
                          height: legendGhostHeight,
                          borderRadius: `${legendGhostTopRadius}px ${legendGhostTopRadius}px ${legendGhostBottomRadius}px ${legendGhostBottomRadius}px`,
                          paddingTop: `${(cellSize * 2) / BASE_CELL_SIZE}px`,
                          position: 'relative'
                        }}
                      >
                        <div
                          className="ghost-eye ghost-eye-left"
                          style={{
                            width: legendEyeSize,
                            height: legendEyeSize,
                            top: legendEyeTop,
                            left: legendEyeLeft
                          }}
                        >
                          <div
                            className="ghost-pupil"
                            style={{
                              width: legendPupilSize,
                              height: legendPupilSize
                            }}
                          />
                        </div>
                        <div
                          className="ghost-eye ghost-eye-right"
                          style={{
                            width: legendEyeSize,
                            height: legendEyeSize,
                            top: legendEyeTop,
                            right: legendEyeRight
                          }}
                        >
                          <div
                            className="ghost-pupil"
                            style={{
                              width: legendPupilSize,
                              height: legendPupilSize
                            }}
                          />
                        </div>
                      </div>
                      <span className={`ghost-legend-label ghost-legend-${ghost.emotion.toLowerCase()}`}>
                        {ghost.emotion}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="touch-controls">
          <button
            className="direction-button direction-button-up"
            onClick={() => handleDirectionChange('UP')}
            aria-label="Move Up"
          >
            ↑
          </button>
          <div className="direction-button-row">
            <button
              className="direction-button direction-button-left"
              onClick={() => handleDirectionChange('LEFT')}
              aria-label="Move Left"
            >
              ←
            </button>
            <button
              className="direction-button direction-button-right"
              onClick={() => handleDirectionChange('RIGHT')}
              aria-label="Move Right"
            >
              →
            </button>
          </div>
          <button
            className="direction-button direction-button-down"
            onClick={() => handleDirectionChange('DOWN')}
            aria-label="Move Down"
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}

export default Game;
